import BaseStore from '@src/utils/store/base-store';
import useMount from '@src/utils/hooks/use-mount';
import useModal from '@src/utils/hooks/use-modal';
import Toast from '@src/components/toast';
import BaseSwiperStore from '@src/components/swiper/base-swiper/store';
import formatRoute from '@src/components/route/format-route';
import routeConfigs from '@src/pages/app-router/route-configs';
import { observable } from 'mobx';
import { autobind } from 'core-decorators';
import { loadReviewContext as requestReviewContext, finishReview as requestFinishReview } from '@src/services/veteran-review';
import { loadTeamContext as requestTeamContext, createTeam as requestCreateTeam } from '@src/services/team';
import MoModalVeteranReviewCreateTeam from '@src/ui-components/mo-modal-veteran-review-create-team';
import VeteranReviewCreateTeamModalStore from './components/create-team-modal/store';
import $$tracker from '@src/tracker';
import { getCreateTeamFailureReason } from '@src/tracker/business-context';

function getErrorMessage(error, fallback = '加载失败，请稍后重试') {
  return error && typeof error.message === 'string' && error.message ? error.message : fallback;
}

function settleRequest(request) {
  return Promise.resolve()
    .then(request)
    .then(value => ({ value }), error => ({ error }));
}

/** 捕获授权一次页面级回顾请求的共享活动事实。 */
function createActivityOwnerSnapshot(activityContext, ownerAccount) {
  return {
    ownerAccount,
    activityStage: activityContext.activityStage,
    playerType: activityContext.playerType
  };
}

/** 把已落定的活动事实绑定到 ensureInitialized 同步选定的 owner 上。 */
function settleActivityContext(activitySessionStore) {
  let initialization;
  try {
    initialization = activitySessionStore.ensureInitialized();
  } catch (error) {
    return { ownerAccount: activitySessionStore.contextOwnerAccount, promise: Promise.resolve({ error }) };
  }
  const ownerAccount = activitySessionStore.contextOwnerAccount;
  return {
    ownerAccount,
    promise: Promise.resolve(initialization).then(
      value => ({
        value,
        // owner 快照在响应提交后构造：冷启动时共享 owner 由本次响应首次登记（null → 账号），
        // 发起时的同步空值不构成"账号更替"；被取代/真变更请求按契约 resolve undefined，由调用方兜底
        ownerSnapshot: value ? createActivityOwnerSnapshot(value, activitySessionStore.contextOwnerAccount) : null
      }),
      error => ({ error })
    )
  };
}

function captureCurrentActivityOwner(activitySessionStore) {
  return createActivityOwnerSnapshot(activitySessionStore, activitySessionStore.contextOwnerAccount);
}

function hasExplicitBrowserAccountChanged(activitySessionStore) {
  return (
    typeof window !== 'undefined' &&
    window.XPASS &&
    typeof window.XPASS.u_account === 'string' &&
    (window.XPASS.u_account || null) !== activitySessionStore.contextOwnerAccount
  );
}

/** 共享会话是否仍代表请求发起时的原账号与角色。 */
function isCurrentActivityOwner(activitySessionStore, snapshot) {
  return (
    snapshot &&
    activitySessionStore.contextOwnerAccount === snapshot.ownerAccount &&
    activitySessionStore.activityStage === snapshot.activityStage &&
    activitySessionStore.playerType === snapshot.playerType
  );
}

/**
 * 解析回顾路由 query 中显式请求的总结视图（海报页关闭回流链路）。
 * HashRouter 下 query 落在 hash 段（如 #/review?view=summary），经路由 history 的 location.search 读取，
 * 与 ended-poster 的来源解析保持同一模式；history 不可读时一律视为未请求。
 *
 * @param {object} history 路由 history 实例（$history）。
 * @returns {boolean} 路由是否显式携带 view=summary。
 */
function isSummaryViewRequested(history) {
  const search = history && history.location && typeof history.location.search === 'string' ? history.location.search : '';
  return new URLSearchParams(search).get('view') === 'summary';
}

class VeteranReviewStore extends BaseStore {
  activitySessionStore;
  loadReviewContext;
  finishReviewRequest;
  loadTeamContext;
  createTeamRequest;

  @observable loadStatus = 'idle';
  @observable stage = 'home';
  @observable reviewStatus = 0;
  @observable.ref sections = [];
  @observable.ref summaryViewModel = {};
  @observable currentReviewIndex = 0;
  @observable finishError = null;
  @observable teamLoadStatus = 'idle';
  @observable.ref teamViewModel = null;

  initializePromise = null;
  initializeOwnerAccount;
  pageExposureReported = false;
  reviewOwnerSnapshot;
  finishPromise = null;
  finishOwnerSnapshot;
  teamLoadPromise = null;
  teamLoadOwnerSnapshot;
  teamViewModelOwnerSnapshot;
  posterActionOwnerSnapshot;
  createTeamModal;

  swiperStore = new BaseSwiperStore({
    swiperOptions: {
      direction: 'vertical',
      loop: false,
      allowTouchMove: true,
      // 1043px 设计稿 slide 经视口缩放；Swiper 默认 50% 慢滑判定阈值在多数手机上达不到
      longSwipesRatio: 0.1,
      // 柔化整页切换节奏；行级入场动效由 active slide 的 CSS 类驱动，见 mo-veteran-review-stages/index.less。
      speed: 500,
      // 自动轮播（2026-09-22 调整）：5s 一页，用户交互即停、末页即停
      autoplay: {
        delay: 5000,
        disableOnInteraction: true,
        stopOnLastSlide: true
      }
    }
  });

  constructor({
    activitySessionStore,
    loadReviewContext = requestReviewContext,
    finishReview = requestFinishReview,
    loadTeamContext = requestTeamContext,
    createTeam = requestCreateTeam
  } = {}) {
    super();
    this.activitySessionStore = activitySessionStore;
    this.loadReviewContext = loadReviewContext;
    this.finishReviewRequest = finishReview;
    this.loadTeamContext = loadTeamContext;
    this.createTeamRequest = createTeam;
    this.createTeamModalStore = new VeteranReviewCreateTeamModalStore({
      createTeamAndEnterPoster: this.createTeamAndEnterPoster,
      enterPoster: this.enterPoster
    });
  }

  /** Registers the page-owned modal and starts the page state machine once. */
  use() {
    super.use();
    this.createTeamModal = useModal(MoModalVeteranReviewCreateTeam, {
      store: this.createTeamModalStore,
      onRequestClose: this.createTeamModalStore.handleExternalClose
    });
    useMount(() => this.initialize());
  }

  get currentPage() {
    return this.sections.length ? this.currentReviewIndex + 1 : 0;
  }

  get totalPages() {
    return this.sections.length;
  }

  get pageText() {
    return `${this.currentPage} / ${this.totalPages}`;
  }

  /**
   * 为当前账号并行加载活动与回顾事实。
   * @returns {Promise<object|undefined>} 已提交的回顾上下文；失败或过期 owner 丢弃后返回 undefined；当前 owner 的错误可能 Toast。
   */
  @autobind
  initialize() {
    const cachedOwnerAccount = this.activitySessionStore.contextOwnerAccount;
    if (this.initializePromise && this.initializeOwnerAccount === cachedOwnerAccount && !hasExplicitBrowserAccountChanged(this.activitySessionStore)) {
      return this.initializePromise;
    }

    // 进页先静默 refresh 一次：强制重拉 get_config 让登录态回到服务端视角（右上角账号条同源）；
    try {
      this.activitySessionStore.refresh().catch(() => undefined);
    } catch (error) {
      // refresh 同步抛错不阻断初始化，仍由 settle 自己的请求负责页面状态
    }
    const activityRequest = settleActivityContext(this.activitySessionStore);
    // 海报页关闭回流场景在路由 query 携带 view=summary；在请求发起时捕获一次，
    // 数据提交段按 reviewStatus 决定是否恢复总结视图
    const requestedSummary = isSummaryViewRequested(this.$history);
    const requestOwnerAccount = activityRequest.ownerAccount;
    let requestOwnerSnapshot = null;
    this.loadStatus = 'loading';
    let request;
    request = Promise.all([activityRequest.promise, settleRequest(() => this.loadReviewContext())])
      .then(([activityResult, reviewResult]) => {
        if (this.initializePromise !== request) {
          return undefined;
        }
        // 被取代的 owner 优先于并行的回顾结果胜出，包括旧账号迟到的 rejection；
        //（冷启动时 owner 由本次响应首次登记，不再与发起时快照比对，2026-09-24 修复静默丢弃）
        if (!activityResult.error && !activityResult.value) {
          this.loadStatus = 'idle';
          return undefined;
        }
        if (activityResult.error) {
          throw activityResult.error;
        }
        requestOwnerSnapshot = activityResult.ownerSnapshot;
        // 活动请求可能先于回顾落定；提交账号级回顾事实前立即复核一次
        if (!isCurrentActivityOwner(this.activitySessionStore, requestOwnerSnapshot)) {
          this.loadStatus = 'idle';
          return undefined;
        }
        if (reviewResult.error) {
          throw reviewResult.error;
        }
        const reviewContext = reviewResult.value;
        this.reviewStatus = reviewContext.reviewStatus;
        this.sections = reviewContext.sections;
        this.summaryViewModel = reviewContext.summary;
        this.reviewOwnerSnapshot = requestOwnerSnapshot;
        this.currentReviewIndex = 0;
        this.swiperStore.activeIndex = 0;
        this.teamLoadStatus = 'idle';
        this.teamViewModel = null;
        this.teamViewModelOwnerSnapshot = undefined;
        this.posterActionOwnerSnapshot = undefined;
        this.stage = 'home';
        this.loadStatus = 'ready';
        // 海报页关闭回流（view=summary）：已完成回顾者直接恢复总结视图并接管 Team 加载；
        // reviewStatus=0（换号或后端重置等异常）忽略参数回落首页；路由已切走时同样不恢复，防止状态追人
        if (requestedSummary && reviewContext.reviewStatus === 1 && isSummaryViewRequested(this.$history)) {
          return this.enterSummary().then(() => reviewContext);
        }
        if (!this.pageExposureReported) {
          $$tracker.pageReviewHome(reviewContext.reviewStatus === 1 ? 'repeat' : 'first');
          this.pageExposureReported = true;
        }
        return reviewContext;
      })
      .catch(error => {
        if (this.initializePromise !== request) {
          return undefined;
        }
        if (
          this.activitySessionStore.contextOwnerAccount !== requestOwnerAccount ||
          (requestOwnerSnapshot && !isCurrentActivityOwner(this.activitySessionStore, requestOwnerSnapshot))
        ) {
          this.loadStatus = 'idle';
          return undefined;
        }
        this.loadStatus = 'error';
        this.stage = 'home';
        Toast.fail(getErrorMessage(error));
        return undefined;
      })
      .then(result => {
        if (this.initializePromise === request) {
          this.initializePromise = null;
          this.initializeOwnerAccount = undefined;
        }
        return result;
      });

    this.initializePromise = request;
    this.initializeOwnerAccount = requestOwnerAccount;
    return request;
  }

  /** Retries initialization from error, or enters the first review section from a ready home. */
  @autobind
  handleStartReview() {
    if (!isCurrentActivityOwner(this.activitySessionStore, this.reviewOwnerSnapshot)) {
      return this.initialize();
    }
    if (this.loadStatus === 'error') {
      return this.initialize();
    }
    if (this.loadStatus !== 'ready' || this.stage !== 'home') {
      return undefined;
    }
    $$tracker.clickStartReview(this.reviewStatus === 1 ? 'repeat' : 'first');
    this.stage = 'review';
    this.handleReviewIndexChange(0);
    return undefined;
  }

  /** Accepts only a real section index and keeps the owner and Swiper index synchronized. */
  @autobind
  handleReviewIndexChange(index) {
    if (this.stage !== 'review' || !Number.isInteger(index) || index < 0 || index >= this.sections.length) {
      return undefined;
    }
    if (this.currentReviewIndex !== index) {
      this.currentReviewIndex = index;
    }
    if (this.swiperStore.activeIndex !== index) {
      this.swiperStore.activeIndex = index;
    }
    return undefined;
  }

  /**
   * 首次浏览的完成门控只通过一次，随后加载总结视图的队伍上下文。
   * @returns {Promise<object|undefined>|undefined} 共享的完成请求、总结切换；当前小节不可完成时返回 undefined。
   */
  @autobind
  completeReview() {
    if (!isCurrentActivityOwner(this.activitySessionStore, this.reviewOwnerSnapshot)) {
      return this.initialize();
    }
    if (this.stage !== 'review' || this.currentReviewIndex !== this.sections.length - 1) {
      return undefined;
    }
    if (this.reviewStatus === 1) {
      return this.enterSummary();
    }
    if (this.finishPromise && isCurrentActivityOwner(this.activitySessionStore, this.finishOwnerSnapshot)) {
      return this.finishPromise;
    }

    const ownerSnapshot = captureCurrentActivityOwner(this.activitySessionStore);
    this.finishError = null;
    let finishResult;
    try {
      finishResult = this.finishReviewRequest();
    } catch (error) {
      finishResult = Promise.reject(error);
    }
    let request;
    request = Promise.resolve(finishResult)
      .then(() => {
        if (this.finishPromise !== request || !isCurrentActivityOwner(this.activitySessionStore, ownerSnapshot)) {
          return undefined;
        }
        this.reviewStatus = 1;
        if (typeof this.activitySessionStore.markReviewCompleted === 'function') {
          this.activitySessionStore.markReviewCompleted();
        }
        return this.enterSummary();
      })
      .catch(error => {
        if (this.finishPromise !== request || !isCurrentActivityOwner(this.activitySessionStore, ownerSnapshot)) {
          return undefined;
        }
        this.finishError = getErrorMessage(error);
        Toast.fail(this.finishError);
        return undefined;
      })
      .then(result => {
        if (this.finishPromise === request) {
          this.finishPromise = null;
          this.finishOwnerSnapshot = undefined;
        }
        return result;
      });

    this.finishPromise = request;
    this.finishOwnerSnapshot = ownerSnapshot;
    return request;
  }

  /** Lets completed viewers skip the review flow entirely and jump straight to the team page, from home or mid-review. */
  @autobind
  handleSkipReview() {
    if (!isCurrentActivityOwner(this.activitySessionStore, this.reviewOwnerSnapshot)) {
      return this.initialize();
    }
    // review 阶段页共用跳过入口（2026-09-20 需求）：home/review 均可跳过；summary 已越过回顾，不再放行
    if (this.loadStatus !== 'ready' || (this.stage !== 'home' && this.stage !== 'review') || this.reviewStatus !== 1) {
      return undefined;
    }
    $$tracker.clickSkipReview();
    this.$history.push(formatRoute(routeConfigs.TEAM.path));
    return undefined;
  }

  /**
   * 总结页左上角返回（2026-09-23 需求）：从总结视图回退到回顾首页（有"前往回顾"按钮的 home）。
   * 纯本地 stage 切换不发请求——回顾数据与 Team ViewModel 均保留，再进总结不重复加载；
   * 再点"前往回顾"由 handleStartReview 重置翻页索引从头观看。
   */
  @autobind
  handleBackToHome() {
    if (this.stage !== 'summary') {
      return undefined;
    }
    this.stage = 'home';
    return undefined;
  }

  /** Enters summary and starts the first authoritative Team lookup. */
  enterSummary() {
    this.stage = 'summary';
    return this.loadTeam();
  }

  /**
   * 加载权威的 Team ViewModel，失败时保留最后一次合法值。
   * @param {Object} options 控制建队刷新失败时是否向弹窗 owner 重抛错误。
   * @param {boolean} options.propagateError 重抛错误而非展示页面级 Toast。
   * @returns {Promise<object|undefined>} 共享的 Team 请求；页面级失败已处理后 resolve undefined。
   */
  @autobind
  loadTeam({ propagateError = false, ownerSnapshot = captureCurrentActivityOwner(this.activitySessionStore) } = {}) {
    if (!isCurrentActivityOwner(this.activitySessionStore, ownerSnapshot)) {
      return Promise.resolve(undefined);
    }
    if (this.teamLoadPromise && isCurrentActivityOwner(this.activitySessionStore, this.teamLoadOwnerSnapshot)) {
      return this.teamLoadPromise;
    }

    this.teamLoadStatus = 'loading';
    let request;
    request = Promise.resolve()
      .then(() => {
        if (!isCurrentActivityOwner(this.activitySessionStore, ownerSnapshot)) {
          return undefined;
        }
        return this.loadTeamContext();
      })
      .then(viewModel => {
        if (this.teamLoadPromise !== request || !isCurrentActivityOwner(this.activitySessionStore, ownerSnapshot)) {
          return undefined;
        }
        this.teamViewModel = viewModel;
        this.teamViewModelOwnerSnapshot = ownerSnapshot;
        this.teamLoadStatus = 'ready';
        return viewModel;
      })
      .catch(error => {
        if (this.teamLoadPromise !== request || !isCurrentActivityOwner(this.activitySessionStore, ownerSnapshot)) {
          return undefined;
        }
        this.teamLoadStatus = 'error';
        if (propagateError) {
          throw error;
        }
        Toast.fail(getErrorMessage(error));
        return undefined;
      })
      .then(
        result => {
          if (this.teamLoadPromise === request) {
            this.teamLoadPromise = null;
            this.teamLoadOwnerSnapshot = undefined;
            if (!isCurrentActivityOwner(this.activitySessionStore, ownerSnapshot)) {
              this.teamLoadStatus = 'idle';
            }
          }
          return result;
        },
        error => {
          if (this.teamLoadPromise === request) {
            this.teamLoadPromise = null;
            this.teamLoadOwnerSnapshot = undefined;
          }
          throw error;
        }
      );

    this.teamLoadPromise = request;
    this.teamLoadOwnerSnapshot = ownerSnapshot;
    return request;
  }

  /**
   * 仅依据权威的 Team 就绪态与成员归属解决总结 CTA。
   * @returns {Promise<object|undefined>|undefined} Team 刷新/路由结果；打开页面持有的弹窗后返回 undefined。
   */
  @autobind
  handlePosterAction() {
    if (!isCurrentActivityOwner(this.activitySessionStore, this.reviewOwnerSnapshot)) {
      return this.initialize();
    }
    if (this.stage !== 'summary') {
      return undefined;
    }
    if (this.teamLoadStatus !== 'ready' || !this.teamViewModel) {
      return this.loadTeam().then(viewModel => {
        if (!viewModel || this.teamLoadStatus !== 'ready') {
          return undefined;
        }
        return this.handlePosterAction();
      });
    }
    if (!isCurrentActivityOwner(this.activitySessionStore, this.teamViewModelOwnerSnapshot)) {
      return this.loadTeam().then(() => undefined);
    }
    $$tracker.clickInviteEntry(this.teamViewModel.hasTeam ? '邀请好友席位' : '生成队伍邀约海报');
    this.posterActionOwnerSnapshot = this.teamViewModelOwnerSnapshot;
    if (this.teamViewModel.hasTeam) {
      return this.enterPoster();
    }

    this.createTeamModalStore.isActive = true;
    this.createTeamModalStore.isOpen = true;
    if (this.createTeamModal) {
      this.createTeamModal.open();
    }
    return undefined;
  }

  /**
   * 创建队伍、刷新成员，只在 hasTeam 权威确认后才路由。
   * @returns {Promise<object>} 确认过的 Team ViewModel；reject 使弹窗保持打开并展示错误。
   */
  @autobind
  createTeamAndEnterPoster() {
    const ownerSnapshot = this.posterActionOwnerSnapshot || captureCurrentActivityOwner(this.activitySessionStore);
    if (!isCurrentActivityOwner(this.activitySessionStore, ownerSnapshot)) {
      return this.initialize().then(() => false);
    }
    let createResult;
    try {
      createResult = this.createTeamRequest();
    } catch (error) {
      createResult = Promise.reject(error);
    }
    return Promise.resolve(createResult)
      .then(
        () => {
          if (!isCurrentActivityOwner(this.activitySessionStore, ownerSnapshot)) {
            return false;
          }
          return this.loadTeam({ propagateError: true, ownerSnapshot });
        },
        error => {
          if (!isCurrentActivityOwner(this.activitySessionStore, ownerSnapshot)) {
            return false;
          }
          throw error;
        }
      )
      .then(viewModel => {
        if (viewModel === false || !isCurrentActivityOwner(this.activitySessionStore, ownerSnapshot)) {
          return false;
        }
        if (!viewModel || viewModel.hasTeam !== true) {
          throw new Error('创建队伍失败，请稍后重试');
        }
        $$tracker.clickCreateTeam('review_summary', 'success', '');
        this.enterPoster();
        return viewModel;
      })
      .catch(error => {
        if (isCurrentActivityOwner(this.activitySessionStore, ownerSnapshot)) {
          $$tracker.clickCreateTeam('review_summary', 'fail', getCreateTeamFailureReason(error));
        }
        throw error;
      });
  }

  /**
   * 老玩家回顾的所有海报分支统一路由到既有海报页。
   * 携带 source=VETERAN_REVIEW：海报页右上角关闭按该来源带 view=summary 精准回流到总结视图。
   * @returns {Promise<object|undefined>|undefined} 当前 owner 的导航；过期流程返回重新初始化结果。
   */
  @autobind
  enterPoster() {
    if (this.posterActionOwnerSnapshot && !isCurrentActivityOwner(this.activitySessionStore, this.posterActionOwnerSnapshot)) {
      return this.initialize();
    }
    this.$history.push(formatRoute(routeConfigs.ENDED_POSTER.path, { source: 'VETERAN_REVIEW' }));
    return undefined;
  }
}

export default VeteranReviewStore;
